import { useEffect, useLayoutEffect, useReducer, useRef, useState } from 'react'
import { useDispatch as useDispatchRedux, useSelector as useSelectorRedux } from 'react-redux'
import { getScrollbarWidth } from './browser'
import { sleep } from './helpers'

export const useSelector = useSelectorRedux
export const useDispatch = useDispatchRedux

/**
 *
 * @returns Whether a component is mounted or not
 */
export function useMounted() {
	const mounted = useRef(false)

	useEffect(() => {
		mounted.current = true
		return () => {
			mounted.current = false
		}
	}, [])
	return mounted.current
}

type HE = HTMLElement
type MOI = MutationObserverInit
/**
 * Use this hook to select native html elements that might not be there at the time of render
 * @param selector Selector supported by document.querySelector
 * @param settings
 * @returns Element if found
 */
export function useQuerySelector<T extends HE = HE>(selector: string, settings?: MOI & { multiple?: false }): T | null
export function useQuerySelector<T extends HE = HE>(
	selector: string,
	settings?: MOI & { multiple: true }
): NodeListOf<T>
export function useQuerySelector<T extends HE = HE>(
	selector: string,
	settings: MOI & { multiple?: boolean }
): T | NodeListOf<T> | null
export function useQuerySelector<T extends HE = HE>(
	selector: string,
	settings: MOI & { multiple?: boolean } = {}
): T | NodeListOf<T> | null {
	const [elements, setElement] = useState(document.querySelectorAll<T>(selector))
	const [, forceUpdate] = useReducer(x => x + 1, 0)

	useLayoutEffect(() => {
		const onChange = async (e?: Event) => {
			try {
				// These function checks shouldn't be needed, but seems to randomly break in some browsers
				if (typeof e?.type.includes === 'function' && e?.type.includes('shopify')) await sleep(100)
				const elem = document.querySelectorAll<T>(selector)
				if (elem && typeof forceUpdate === 'function') forceUpdate()
				setElement(elem)
			} catch (error) {
				console.warn(error)
			}
		}
		const observer = new MutationObserver(_ => onChange())
		elements.forEach(element => {
			element.addEventListener('change', onChange)
			observer.observe(element, {
				attributes: true,
				childList: false,
				characterData: false,
				...settings
			})
		})

		const events = [
			'locationchange',
			'DOMContentLoaded',
			'shopify:section:load',
			'shopify:section:reorder',
			'shopify:section:unload'
		] as const
		events.forEach(evt => window.addEventListener(evt, onChange))
		return () => {
			events.forEach(evt => window.removeEventListener(evt, onChange))
			observer.disconnect()
			elements.forEach(element => element.removeEventListener('change', onChange))
		}
	}, [selector, elements, settings])
	return settings.multiple ? elements : elements[0] ?? null
}

export function useWindowSize() {
	const [windowSize, setWindowSize] = useState({
		width: window.innerWidth,
		height: window.innerHeight
	})

	useEffect(() => {
		// Handler to call on window resize
		function handleResize() {
			// Set window width/height to state
			setWindowSize({
				width: window.innerWidth,
				height: window.innerHeight
			})
		}

		window.addEventListener('resize', handleResize, { passive: true })
		return () => window.removeEventListener('resize', handleResize)
	}, [])

	return windowSize
}

export function useScrollbarWidth() {
	const ref = useRef(0)
	useLayoutEffect(() => {
		ref.current = getScrollbarWidth()
	})
	return ref.current
}
